import React from 'react';
import { withStyles, Box, SubAccountBalanceView, PulloverSelect, formattedCurrency, Tooltip } from 'vopay';
import { Title } from 'UI';
import { useTranslation } from 'react-i18next';
import styles from './BalanceDisplayCard.module.scss';

const classes = withStyles(styles);

interface BalanceItem {
  currency: string;
  availableBalance: string;
  rollupBalance?: string;
}

interface BalanceDisplayCardProps {
  balances: BalanceItem[];
  displayBalance: SubAccountBalanceView;
  selectedBalanceType: SubAccountBalanceView;
  onBalanceTypeChange?: (balanceType: SubAccountBalanceView) => void;
  showRollUpBalance?: boolean; // Whether to show the balance type selector
}

const BalanceDisplayCard: React.FC<BalanceDisplayCardProps> = ({
  balances,
  displayBalance,
  selectedBalanceType,
  onBalanceTypeChange,
  showRollUpBalance = true,
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.List.OrgChart.BalanceDisplay' });

  // Tooltip content for balance information
  const rollUpBalanceTooltip = (
    <Box width="280px" flx="col" gapSize="8px">
      <Title level="6">{t('rollUpBalanceTooltipTitle', { defaultValue: 'Roll Up Balance' })}</Title>
      <div style={{ fontSize: '15px', lineHeight: '20px' }}>
        {t('rollUpBalanceTooltipDescription', {
          defaultValue:
            'This balance is a visual summary of all sub-accounts, calculated as the total of their balances. Funds cannot be moved from this account.',
        })}
      </div>
    </Box>
  );

  const availableBalanceTooltip = (
    <Box width="280px" flx="col" gapSize="8px">
      <Title level="6">{t('availableBalanceTooltipTitle', { defaultValue: 'Available Balance' })}</Title>
      <div style={{ fontSize: '15px', lineHeight: '20px' }}>
        {t('availableBalanceTooltipDescription', {
          defaultValue: 'This balance represents the funds currently available for use.',
        })}
      </div>
    </Box>
  );

  // Get balance value based on type
  const getBalanceValue = (balance: BalanceItem, type: SubAccountBalanceView): string => {
    if (type === 'available-balance') {
      return balance.availableBalance || '0';
    }
    if (type === 'roll-up-balance') {
      return balance.rollupBalance || '0';
    }
    return '0';
  };

  // Don't render if balance should not be shown
  if (displayBalance === 'do-not-show-balances') {
    return null;
  }

  return (
    <div className={classes('balanceDisplay')}>
      <div className={classes('balanceCard')}>
        <div className={classes('balanceContent')}>
          <>
            <PulloverSelect
              value={selectedBalanceType}
              buttonStyle={{
                fontSize: '13px',
                '--iconSize': '13px',
                '--height': 'auto',
                '--paddingStart': '0px',
                '--paddingEnd': '0px',
                marginBottom: '2px',
              }}
              onChange={(value) => onBalanceTypeChange && onBalanceTypeChange(value as SubAccountBalanceView)}
            >
              <PulloverSelect.Option value="available-balance">
                {t('availableBalanceLabel', { defaultValue: 'Available Bal.' })}
              </PulloverSelect.Option>
              <PulloverSelect.Option value="roll-up-balance">
                {t('rollUpBalanceLabel', { defaultValue: 'Roll Up Bal.' })}
              </PulloverSelect.Option>
            </PulloverSelect>
            {showRollUpBalance || selectedBalanceType === 'available-balance'
              ? balances.map((balance: BalanceItem) => (
                  <div key={balance.currency} className={classes('balanceAmountRow')}>
                    {balance.currency} {formattedCurrency(getBalanceValue(balance, selectedBalanceType))}
                  </div>
                ))
              : balances.map((balance: BalanceItem) => (
                  <div key={balance.currency} className={classes('balanceAmountRow')}>
                    N/A
                  </div>
                ))}
          </>
        </div>
        <Tooltip
          information={selectedBalanceType === 'roll-up-balance' ? rollUpBalanceTooltip : availableBalanceTooltip}
          icon="info"
          buttonWidth="16px"
          iconWidth="12px"
          hasArrow
          position="top"
          variant="outlined"
        />
      </div>
    </div>
  );
};

export default BalanceDisplayCard;
